import type { AppPolicy, Claims } from '@helios/authz';

/**
 * Roles provisioned for web-admin tenants (from the RBAC CSV).
 * Used only to validate that a signed-in user carries a recognizable role
 * claim; actual authorization is permissions-derived (Firestore role docs),
 * not this list.
 */
export const KNOWN_ROLES = ['admin', 'doctor', 'front_desk', 'pharmacist'] as const;

/**
 * PRD.293 — operational roles keep clinic-wide visibility. Holding ANY of these
 * disables provider-scoping, even if the user is also a doctor. The named PRD
 * roles that don't yet exist as claims (billing, finance, inventory, super
 * admin) are listed too so scoping stays correct if/when they're provisioned.
 */
export const OPERATIONAL_ROLES = [
  'admin',
  'front_desk',
  'pharmacist',
  'billing',
  'finance',
  'inventory',
  'super_admin',
] as const;

/**
 * PRD.293 — a "pure doctor" is scoped to their own linked provider: carries the
 * `doctor` role and NONE of the operational roles. Everyone else (operational
 * users, multi-role admins-who-are-also-doctors) stays clinic-wide, so there is
 * no regression for existing users. Pure function — unit-testable without React.
 */
export function isProviderScopedRoles(roles: readonly string[] | null | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  if (!roles.includes('doctor')) return false;
  const operational = OPERATIONAL_ROLES as readonly string[];
  return !roles.some((r) => operational.includes(r));
}

/**
 * Legacy app policy, retained for the No-Access screen's debug display.
 * PRD.268 replaced role-list entry gating with permissions-derived entry
 * (see hasValidRoleClaim + PermissionProvider.hasAccess in App.tsx).
 */
export const APP_POLICY: AppPolicy = {
  appId: 'web-admin',
  entryAnyOfRoles: [...KNOWN_ROLES],
} as const;

/**
 * True if the user has the minimum identity claims to *attempt* entry: an
 * active org and at least one role in it. The final entry decision is made
 * after permissions load (must grant at least one accessible module).
 */
export function hasValidRoleClaim(claims: Claims | null | undefined): boolean {
  if (!claims) return false;
  const { orgs, activeOrg } = claims;
  if (!activeOrg || !orgs) return false;
  const roles = orgs[activeOrg]?.roles;
  return Array.isArray(roles) && roles.length > 0;
}
